/**
 * «Официанты кассы» — кто скрывается за кодом в чеке.
 *
 * Зачем эта вкладка появилась 29.09.2026. Касса присылает справочник
 * сотрудников каждые десять минут — и код, и имя, — но до этого дня его
 * не читал никто. Поле `rk7_employee_code` существовало с первой
 * миграции, по нему соединяются пять модулей финансового агента, а
 * заполнить его было нечем: ни экрана, ни адреса. Из-за этого «Выручка
 * по официантам» печатала код вместо имени, витрина показателей
 * официанта стояла пустой, и персональный наставник обучающего агента
 * не получал данных вовсе.
 *
 * **Сама система не связывает.** §7.3 ТЗ платформы запрещает
 * сопоставлять по ФИО автоматически, и запрет правильный: тёзки в
 * ресторане — обычное дело, а ошибка припишет выручку одного официанта
 * другому и покажет её ему как его собственную. Поэтому здесь —
 * подсказка и одно касание, а решает человек. Тот же порядок, что у
 * заявок на доступ (D234).
 *
 * Показываются все коды кассы, включая выключенные: чеки уволенного
 * остаются в прошлом месяце, и его показатели должны лечь на него.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Empty, ErrorState, Loading } from '@shell/states';
import { MayEditTeam } from './access';
import { TEAM_KEYS, problemText, teamApi } from './api';
import type { Waiter, WaitersScreen } from './api';
import { useContext } from 'react';

/** Как называется степень совпадения. Число человеку ничего не скажет. */
const MATCH_TITLES: Record<string, string> = {
  exact: 'имя совпадает',
  partial: 'совпала фамилия или имя',
  none: '',
};

export function Waiters() {
  const client = useQueryClient();
  const mayEdit = useContext(MayEditTeam);
  const [problem, setProblem] = useState<string | null>(null);

  const waiters = useQuery<WaitersScreen, ApiError>({
    queryKey: TEAM_KEYS.waiters,
    queryFn: () => teamApi.waiters(),
  });

  const save = useMutation<unknown, ApiError, { code: string; employeeId: number | null }>({
    mutationFn: ({ code, employeeId }) => teamApi.linkWaiter(code, employeeId),
    onSuccess: () => {
      setProblem(null);
      client.invalidateQueries({ queryKey: TEAM_KEYS.waiters });
    },
    onError: (error) => setProblem(problemText(error)),
  });

  if (waiters.isLoading) return <Loading />;
  if (waiters.error) return <ErrorState error={waiters.error} />;

  const rows = waiters.data?.waiters ?? [];
  if (rows.length === 0) {
    return (
      <Empty
        title="Справочник кассы ещё не приезжал"
        text="Он приходит вместе с продажами. Если продажи идут, а списка нет — проверьте канал до ресторана."
      />
    );
  }

  const linked = rows.filter((row) => row.employee_id !== null).length;

  return (
    <div className="waiters">
      <p className="waiters__lead">
        Касса подписывает каждый чек кодом. Пока код не закреплён за сотрудником, отчёты
        показывают код вместо имени, а показатели официанта не считаются.{' '}
        <strong>
          Закреплено {linked} из {rows.length}.
        </strong>
      </p>
      {problem && <p className="waiters__problem">{problem}</p>}
      <ul className="waiters__list">
        {rows.map((row) => (
          <Row
            key={row.code}
            row={row}
            mayEdit={mayEdit}
            busy={save.isPending}
            onLink={(employeeId) => save.mutate({ code: row.code, employeeId })}
          />
        ))}
      </ul>
    </div>
  );
}

function Row({
  row,
  mayEdit,
  busy,
  onLink,
}: {
  row: Waiter;
  mayEdit: boolean;
  busy: boolean;
  onLink: (employeeId: number | null) => void;
}) {
  const name = row.till_name ?? row.till_code ?? row.code;
  return (
    <li className={`waiter${row.employee_id === null ? ' waiter--free' : ''}`}>
      <div className="waiter__head">
        <span className="waiter__name">{name}</span>
        {/*
          Показываем номер станции, а не ключ чека: официанта в
          ресторане знают по первому, а второй («1000037») ему ничего
          не говорит. Связывается при этом ключ — он уезжает на сервер.
        */}
        <span className="waiter__code">код {row.till_code ?? row.code}</span>
        {!row.is_active && <span className="waiter__off">выключен в кассе</span>}
      </div>

      {row.employee_id !== null ? (
        <div className="waiter__linked">
          <span>{row.employee_name}</span>
          {mayEdit && (
            <button
              type="button"
              className="button button--ghost"
              disabled={busy}
              onClick={() => onLink(null)}
            >
              Открепить
            </button>
          )}
        </div>
      ) : (
        <div className="waiter__pick">
          {row.candidates.length === 0 ? (
            <span className="waiter__hint">Свободных сотрудников нет — сначала заведите человека.</span>
          ) : (
            row.candidates.map((candidate) => (
              <button
                key={candidate.employee_id}
                type="button"
                className={`chip${candidate.match === 'exact' ? ' chip--active' : ''}`}
                disabled={!mayEdit || busy}
                title={MATCH_TITLES[candidate.match] || undefined}
                onClick={() => onLink(candidate.employee_id)}
              >
                {candidate.full_name}
                {candidate.position_name && (
                  <span className="chip__count">{candidate.position_name}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </li>
  );
}
